/**
 * What of a TinyMCE body counts as painted text. TinyMCE keeps scaffolding
 * of its own inside the body — a caret container beside a non-editable
 * block, a bogus trailing paragraph — marked `data-mce-bogus`; its
 * serializer drops them, so they never reach the preview or the PDF, but
 * they are laid out on the canvas and a height read off the body includes
 * them. On a saved page, an 80pt name on one line read 125px at settle (its
 * 107 plus one line at the base size), was fitted for it, and painted one
 * line the moment the scaffold went.
 */
export const BOGUS_SELECTOR = '[data-mce-bogus]'

export const bogusElementsUnder = (body: HTMLElement): HTMLElement[] =>
  Array.from(body.querySelectorAll<HTMLElement>(BOGUS_SELECTOR))

/**
 * The body's painted height with its bogus scaffolding out of the layout
 * for the read — the height the same HTML paints on every other surface.
 * The scaffolding is hidden for the read and restored, so the caret and
 * TinyMCE's own bookkeeping are left as they were.
 */
export const paintedContentHeight = (body: HTMLElement): number => {
  const bogus = bogusElementsUnder(body)
  if (bogus.length === 0) return body.offsetHeight
  const displays = bogus.map((element) => element.style.display)
  bogus.forEach((element) => {
    element.style.display = 'none'
  })
  const height = body.offsetHeight
  bogus.forEach((element, index) => {
    element.style.display = displays[index]
  })
  return height
}

/**
 * `html` without the bogus scaffolding, for the measurer: what the serializer
 * would hand any other surface. It is parsed in a document of its own, which
 * has no window, so nothing in it loads or runs while the scaffolding is
 * taken out (an `<img onerror>` fires in a detached element of the page's
 * own document).
 */
export const withoutBogus = (html: string): string => {
  if (!html.includes('data-mce-bogus')) return html
  const scratch = document.implementation.createHTMLDocument('').body
  scratch.innerHTML = html
  scratch
    .querySelectorAll(BOGUS_SELECTOR)
    .forEach((element) => element.remove())
  return scratch.innerHTML
}

const HEAD_LENGTH = 80

/**
 * The body's blocks at the instant of a read, for the stamp: how many
 * children the body holds, how many bogus elements are under it, and the
 * head of the last child's markup — enough to name an extra line box.
 */
export const bodyBlocksStamp = (body: HTMLElement): string => {
  const last = body.lastElementChild
  const head = last ? last.outerHTML.slice(0, HEAD_LENGTH) : ''
  return `${body.children.length}:${bogusElementsUnder(body).length}:${head}`
}

const INNER_HEAD_LENGTH = 240
const CHIP_SELECTOR =
  '[data-case-detail], .mceNonEditable, [data-editor-empty-field]'
const CHIPS_LISTED = 12

const codePointsOf = (text: string): string =>
  Array.from(text)
    .map((char) => `U+${char.codePointAt(0)!.toString(16).toUpperCase()}`)
    .join('+')

/** How many distinct line boxes `block`'s contents occupy, by the tops of their client rects; 0 where nothing lays out. */
const lineCountOf = (block: Element): number => {
  const range = block.ownerDocument.createRange()
  if (typeof range.getClientRects !== 'function') return 0
  range.selectNodeContents(block)
  const tops = new Set<number>()
  Array.from(range.getClientRects()).forEach((rect) => {
    if (rect.height > 0) tops.add(Math.round(rect.top * 2) / 2)
  })
  return tops.size
}

/**
 * The body's anatomy at the instant of a read, for the stamp that names an
 * extra line box the blocks stamp cannot: the body's vertical padding, its
 * root nodes (elements against all nodes, and the code points of any stray
 * text at the root, which lays out as a block of its own at the body's base
 * size), each block's painted height and line count, every case-detail chip
 * that takes height (its key, computed display and height), and the head of
 * the last block's inner markup.
 */
export const bodyAnatomyStamp = (body: HTMLElement): string => {
  const view = body.ownerDocument.defaultView
  const computed = view?.getComputedStyle(body)
  const pad = computed
    ? `${computed.paddingTop}/${computed.paddingBottom}`
    : 'none'
  const stray = Array.from(body.childNodes)
    .filter((node) => node.nodeType !== Node.ELEMENT_NODE)
    .map((node) => `${node.nodeName}:${codePointsOf(node.textContent ?? '')}`)
    .join(',')
  const blocks = Array.from(body.children)
    .map(
      (block) =>
        `${block.tagName.toLowerCase()}=${(block as HTMLElement).offsetHeight}/${lineCountOf(block)}`,
    )
    .join(',')
  const chips = Array.from(body.querySelectorAll<HTMLElement>(CHIP_SELECTOR))
  const visible = chips.filter((chip) => chip.offsetHeight > 0)
  const listed = visible
    .slice(0, CHIPS_LISTED)
    .map((chip) => {
      const key =
        chip.getAttribute('data-case-detail') ??
        chip.getAttribute('data-editor-empty-field') ??
        chip.className
      const display = view?.getComputedStyle(chip).display ?? '?'
      return `${key}=${display}/${chip.offsetHeight}`
    })
    .join(',')
  const last = body.lastElementChild
  const inner = last ? last.innerHTML.slice(0, INNER_HEAD_LENGTH) : ''
  return [
    `pad=${pad}`,
    `nodes=${body.children.length}/${body.childNodes.length}`,
    `stray=${stray}`,
    `blocks=${blocks}`,
    `chips=${visible.length}/${chips.length}${listed ? `:${listed}` : ''}`,
    `inner=${inner}`,
  ].join(';')
}
